import Button from 'components/Form/Button';
import Check from 'components/Form/Check';
import { CoreContext } from 'context/CoreContext';
import moment from 'moment';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import {
  Create,
  Update,
  baixaParcela,
  msgRenegociacaoCliente,
} from 'services/cobranca';
import {
  optionsPeriodicidade,
  optionsPrazoRenogicar,
  optionsSN,
  optionsTacTab,
} from 'utils/options';
import { parseCurrency, unparseCurrency } from 'utils/parsers';
import { calculaCobranca, taxaCET } from 'utils/simuladorFin';
import FormCore from '../../Form/Core';
import BasicTable from '../../Form/Table';
import WrapperTwoCol from '../WrapperTwoCol';
import {
  AddProposalContainer,
  ButtonsAndInputWrapper,
  CompanyButtonContainer,
} from './styled';

export default function BankingRenegociar({ dados, reload }) {
  const { user, setModal } = useContext(CoreContext);
  const [loading, setLoading] = useState(false);
  const [registers, setRegisters] = useState([dados.titulos]);
  const formOfer = useRef(null);
  const [allSelect, setAllSelect] = useState(false);
  const [confirma, setConfirma] = useState(dados.confirma_cliente);
  const [recalcula, setRecalcula] = useState(false);

  const close = () => setModal(null);

  const init = useCallback(async () => {
    setLoading(true);
    try {
      const saldoVencerFilter = dados.titulos
        .filter((a) => a.status === 'aberto')
        .map((a) => {
          return a.saldo_devedor;
        });

      //filtra se ja nao existe o saldo a vencer para adicionar, caso nao exista adiciona o saldo a vencer como parcela para renegociar.
      if (!dados.titulos.some((a) => a.parcela === 'Saldo a Vencer')) {
        dados.titulos.push({
          id: 0,
          parcela: 'Saldo a Vencer',
          renegociar: dados.renegociacao_saldodevedor,
          valor_renegociar: undefined,
          status: 'vencido',
          valor_juros_mora: !saldoVencerFilter[0] ? 0 : saldoVencerFilter[0],
          capital: 0,
          data_baixa: null,
          data_vencimento: undefined,
          dias_vencido: null,
          saldo_devedor: !saldoVencerFilter[0] ? 0 : saldoVencerFilter[0],
          tipo_baixa: null,
          valor_iof_juros: 0,
          valor_juros_remun: 0,
          valor_multa: 0,
          valor_recebido: 0,
        });
      }
      const data = dados.titulos;
      if (!data.length) {
        setRegisters([]);
        setLoading(false);
        return;
      }
      const sorted = data.sort((a, b) => (b.parcela || 0) + (a.parcela || 0));
      const next = sorted.filter((item) => item?.status !== 'baixado');
      setRegisters(next);
    } catch (err) {
      console.error('Erro ao carregar propostas:', err);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [user]);

  //informacaoes da tabela.
  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: 'Parcela',
        ref: 'parcela',
        renderCell: ({ row }) => {
          const status = row?.status;
          const parcela = row?.parcela;
          if ((status === 'vencido') & (parcela === 'Saldo a Vencer')) {
            return parcela;
          } else {
            return <span style={{ color: 'blue' }}>{parcela}</span>;
          }
        },
      },
      { title: 'Dias Vencido', ref: 'dias_vencido' },
      {
        title: 'Vencimento',
        ref: 'data_vencimento',
        renderCell: ({ row }) => {
          const status = row?.status;
          const data = !row?.data_vencimento
            ? '-'
            : moment(row?.data_vencimento).format('L');
          if (status === 'vencido') {
            return <span style={{ color: 'red' }}>{data}</span>;
          }
          return data;
        },
      },
      { title: 'PMT', ref: 'pmttitulo' },
      { title: 'Mora', ref: 'mora' },
      {
        align: 'center',
        title: (
          <div
            style={{ display: 'flex', justifyContent: 'center', width: '100%' }}
          >
            <Check
              align="center"
              onChange={async () => {
                if (confirma === false) {
                  let botao;
                  for (let todos of dados.titulos) {
                    if (todos.status === 'vencido') {
                      let check = !allSelect;
                      if (check === true) {
                        botao = true;
                        todos.renegociar = true;
                      } else {
                        botao = false;
                        todos.renegociar = false;
                      }
                    }
                  }

                  setRegisters([...dados.titulos]);
                  await setAllSelect(botao);

                  calc();
                  calcCet();
                }
              }}
              checked={allSelect}
            />
          </div>
        ),
        ref: 'renegociar',
        renderCell: ({ row }) => (
          <Check
            onChange={async () => {
              if (confirma === false) {
                for (let linhaparcela of dados.titulos) {
                  if (linhaparcela.parcela === row.parcela) {
                    linhaparcela.renegociar = !linhaparcela.renegociar;
                  }
                }
                setRegisters([...dados.titulos]);

                calc();
                calcCet();
              }
            }}
            checked={row.renegociar}
            label={''}
            align="center"
          />
        ),
      },
    ];
    return baseColumns;
  }, [registers]);

  //variaveis que vao na tabela.
  const rows = useMemo(() => {
    return (registers || [])
      .filter((a) => a.status === 'vencido')
      .map((a) => ({
        id: a?.id,
        data_vencimento: a?.data_vencimento,
        parcela: a?.parcela,
        pmttitulo: parseCurrency(a?.valor),
        pmttotal: parseCurrency(
          a?.valor_juros_remun +
            a?.capital +
            a?.valor_juros_mora +
            a?.valor_multa +
            a?.valor_iof_juros
        ),
        mora: parseCurrency(
          a?.valor_juros_mora + a?.valor_multa + a?.valor_iof_juros
        ),
        status: a?.status,
        renegociar: !a?.renegociar ? false : a?.renegociar,
        valor_juros_mora: a?.valor_juros_mora,
        valor_multa: a?.valor_multa,
        valor_juros_remun: a?.valor_juros_remun,
        capital: a?.capital,
        valor_iof_juros: a?.valor_iof_juros,
        valor_renegociar: a?.valor_renegociar,
        dias_vencido: !a?.dias_vencido ? '-' : a?.dias_vencido,
      }));
  }, [registers]);

  useEffect(() => init(), [init]);

  //campos que para fazer a renegociacao e ver informações
  const formOfertaInitial = [
    {
      ref: 'valor_renegociar',
      placeholder: 'Valor Renegociação (R$)',
      tress: true,
      type: 'money',
      required: false,
      disabled: true,
      onBlur: () => {
        if (!formOfer.current) {
          return;
        }
        calc();
        calcCet();
      },
    },
    {
      ref: 'valor_entrada',
      placeholder: 'Valor de Entrada (R$)',
      tress: true,
      type: 'money',
      required: false,
      disabled: confirma,
      onBlur: async () => {
        if (!formOfer.current) {
          return;
        }

        setRecalcula((prev) => !prev);
        await calc();
        await calcCet();
      },
    },
    {
      ref: 'dias_carencia',
      placeholder: 'Data 1º Parcela',
      tress: true,
      type: 'date',
      disabled: confirma,
      transparent: true,
      onBlur: () => {
        if (!formOfer.current) {
          console.log('formOfer.current is null');
          return;
        }
        setRecalcula((prev) => !prev); //state to trigger useEffect pra recalcular quando a data de carencia mudar, da de usar em outros campos caso precise
        calc();
        calcCet();
      },
    },

    {
      ref: 'taxa',
      placeholder: 'Taxa %',
      tress: true,
      type: 'number',
      required: false,
      disabled: confirma,
      onBlur: async () => {
        if (!formOfer.current) {
          return;
        }
        setRecalcula((prev) => !prev);
        await calc();
        await calcCet();
      },
    },
    {
      ref: 'periodicidade',
      placeholder: 'Periodicidade',
      tress: true,
      type: 'select',
      options: optionsPeriodicidade,
      required: false,
      disabled: confirma,
      onBlur: async () => {
        if (!formOfer.current) {
          return;
        }

        await calc();
        await calcCet();
      },
    },
    {
      ref: 'prazo',
      placeholder: 'Prazo',
      tress: true,
      type: 'select',
      options: optionsPrazoRenogicar,
      required: false,
      disabled: confirma,
      onBlur: async () => {
        if (!formOfer.current) {
          return;
        }

        await calc();
        await calcCet();
      },
    },
    {
      ref: 'tacSN',
      placeholder: '% TAC?',
      tress: true,
      type: 'select',
      options: optionsTacTab,
      required: false,
      disabled: confirma,
      transparent: true,
      onBlur: async () => {
        if (!formOfer.current) {
          return;
        }
        await calc();
        await calcCet();
      },
    },
    {
      ref: 'seguroSN',
      placeholder: 'Seguro Prestamista?',
      tress: true,
      type: 'select',
      options: optionsSN,
      required: false,
      disabled: confirma,
      transparent: true,
      onBlur: async () => {
        if (!formOfer.current) {
          return;
        }
        await calc();
        await calcCet();
      },
    },
    {
      ref: 'seguroAssisSN',
      placeholder: 'Seguro Assistência?',
      tress: true,
      type: 'select',
      options: optionsSN,
      required: false,
      disabled: confirma,
      transparent: true,
      onBlur: async () => {
        if (!formOfer.current) {
          return;
        }
        await calc();
        await calcCet();
      },
    },

    {
      ref: 'tac',
      placeholder: 'TAC',
      tress: true,
      type: 'money',
      required: false,
      disabled: true,
      transparent: false,
    },
    {
      ref: 'valor_seguro',
      placeholder: 'Valor Seguro Prestamista',
      tress: true,
      type: 'select',
      required: false,
      disabled: true,
      transparent: false,
    },
    {
      ref: 'valor_seguroAssis',
      placeholder: 'Valor Seguro Assistência',
      tress: true,
      type: 'select',
      required: false,
      disabled: true,
      transparent: false,
    },

    {
      ref: 'iofSN',
      placeholder: 'IOF?',
      tress: true,
      type: 'select',
      options: optionsSN,
      required: false,
      disabled: confirma,
      transparent: true,
      onBlur: async () => {
        if (!formOfer.current) {
          return;
        }
        await calc();
        await calcCet();
      },
    },
    {
      ref: 'tabSN',
      placeholder: '% TAB?',
      tress: true,
      type: 'select',
      options: optionsTacTab,
      required: false,
      disabled: confirma,
      transparent: true,
      onBlur: async () => {
        if (!formOfer.current) {
          return;
        }
        await calc();
        await calcCet();
      },
    },
    {
      ref: 'cet',
      placeholder: 'CET %',
      tress: true,
      type: 'number',
      required: false,
      disabled: true,
      transparent: false,
    },
    {
      ref: 'iof',
      placeholder: 'IOF',
      tress: true,
      type: 'select',
      required: false,
      disabled: true,
      transparent: false,
    },
    {
      ref: 'tab',
      placeholder: 'TAB',
      tress: true,
      type: 'select',
      required: false,
      disabled: true,
      transparent: false,
    },

    {
      ref: 'pmt',
      placeholder: 'PMT',
      tress: true,
      type: 'money',
      required: false,
      disabled: true,
      transparent: false,
    },
    {
      ref: 'carteira',
      placeholder: 'Bancarizar?',
      half: true,
      type: 'select',
      options: optionsSN,
      disabled: confirma,
    },
  ];

  const [formOferta, setFormOferta] = useState(formOfertaInitial);

  const atualizarPlaceholder = (ref, novoPlaceholder) => {
    setFormOferta((prev) =>
      prev.map((item) =>
        item.ref === ref ? { ...item, placeholder: novoPlaceholder } : item
      )
    );
  };

  //faz o calculos dos juros
  const calc = useCallback(async () => {
    const formValues = formOfer.current.getForm?.();
    const perc_iof = dados?.perc_iof;
    const perc_tac = dados?.perc_tac;
    const taxa = dados?.taxas;
    const taxas = dados?.taxasbanco;
    const taxasiof = dados?.taxasiof;
    const taxabanco = {
      perc_iof: perc_iof,
      perc_tac: perc_tac,
      taxas: taxa,
      taxasbanco: taxas,
    };
    if (!taxabanco) {
      return;
    }

    // Ajusta a taxa conforme o valor do campo taxa do formulário
    const taxaAjustada = {
      ...taxabanco,
      perc_taxa: parseFloat(formValues.taxa),
      perc_tabSN: parseFloat(formValues.tabSN),
      perc_tacSN: parseFloat(formValues.tacSN),
      taxasiof: taxasiof,
    };
    const resultados = calculaCobranca(formValues, taxaAjustada);

    // Atualiza os campos do formulrio com os resultados calculados

    formOfer.current.setItem(parseCurrency(resultados.valorParcela), 'pmt');
    formOfer.current.setItem(parseCurrency(resultados.valorIof), 'iof');
    formOfer.current.setItem(
      parseCurrency(resultados.valorSeguro),
      'valor_seguro'
    );
    formOfer.current.setItem(
      parseCurrency(resultados.valorSeguroAssis),
      'valor_seguroAssis'
    );
    formOfer.current.setItem(parseCurrency(resultados.valorTab), 'tab');
    formOfer.current.setItem(parseCurrency(resultados.valorTac), 'tac');

    dados.renegociacao_periodicidade =
      formOfer.current.getItem('periodicidade');

    dados.renegociacao_data_1parcela =
      formOfer.current.getItem('dias_carencia');
    dados.carteira = formOfer.current.getItem('carteira');
    dados.renegociacao_entrada = formOfer.current.getItem('valor_entrada');

    dados.renegociacao_prazo = formOfer.current.getItem('prazo');
    dados.renegociacao_taxa = formOfer.current.getItem('taxa');
    dados.renegociacao_seguro =
      formOfer.current.getItem('seguroSN') == 'Sim' ? true : false;
    dados.renegociacao_seguro_assistencia =
      formOfer.current.getItem('seguroAssisSN') == 'Sim' ? true : false;
    dados.renegociacao_iof =
      formOfer.current.getItem('iofSN') == 'Sim' ? true : false;
    dados.renegociacao_tab = formOfer.current.getItem('tabSN');
    dados.renegociacao_tac = formOfer.current.getItem('tacSN');
  }, [registers, allSelect]);

  //calcula a cet
  const calcCet = async () => {
    let prazo = parseInt(formOfer.current.getItem('prazo'));
    let pmt = unparseCurrency(formOfer.current.getItem('pmt'));
    let valorFinan =
      formOfer.current.getItem('valor_renegociar') -
      formOfer.current.getItem('valor_entrada');
    let cet = await taxaCET(prazo, pmt, valorFinan * -1);
    formOfer.current.setItem(cet, 'cet');
  };

  //traz os dados dos campos e quando alterado refaz os calculos, caso seja vazio traz um padrao.
  useEffect(() => {
    //verifica nos array qual esta selecionado para somar.
    const renegociar = rows.reduce((a, item) => {
      if (item.renegociar === true) {
        let valor;

        if (!item.valor_renegociar) {
          //valor = unparseCurrency(item.mora) + item?.capital
          valor = unparseCurrency(item.mora) + unparseCurrency(item.pmttitulo);
        } else {
          valor = item.valor_juros_mora;
        }
        return a + valor;
      }
      return a;
    }, 0);
    const today = new Date();
    const data1parcela = moment(today).add(30, 'days').format('YYYY-MM-DD');

    const dataParcela = new Date(formOfer.current.getItem('dias_carencia'));
    const diacarencia =
      Math.floor(
        (dataParcela.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
      ) + 1;

    if (diacarencia < 0) {
      toast.error(
        'A data da primeira parcela não pode ser menor que a data atual'
      );
    }
    const valorEntrada = formOfer.current.getItem('valor_entrada');
    const taxa = formOfer.current.getItem('taxa');
    const calculo = (taxa / 30) * diacarencia;
    const valorRenegociar = (calculo / 100) * (renegociar - valorEntrada);
    const renegociarValor = valorRenegociar + renegociar;

    formOfer.current.setItem(renegociar, 'valor_renegociar');

    if (!formOfer.current.getItem('valor_entrada')) {
      formOfer.current.setItem(0, 'valor_entrada');
      formOfer.current.setItem(renegociarValor, 'valor_total');
    } else {
      const valorEntrada = formOfer.current.getItem('valor_entrada');
      formOfer.current.setItem(renegociarValor - valorEntrada, 'valor_total');
    }

    formOfer.current.setItem(
      !dados.carteira ? 'Não' : dados.carteira,
      'carteira'
    );
    formOfer.current.setItem(
      !dados.renegociacao_tab ? dados.taxasiof.tab : dados.renegociacao_tab,
      'tabSN'
    );
    formOfer.current.setItem(
      !dados.renegociacao_tac ? dados.taxasiof.tac : dados.renegociacao_tac,
      'tacSN'
    );
    formOfer.current.setItem(
      !dados.renegociacao_data_1parcela
        ? data1parcela
        : dados.renegociacao_data_1parcela,
      'dias_carencia'
    );
    formOfer.current.setItem(
      !dados?.renegociacao_entrada ? 0 : dados?.renegociacao_entrada,
      'valor_entrada'
    );
    formOfer.current.setItem(
      String(
        !dados?.renegociacao_prazo
          ? !dados?.taxas[0].prazo
            ? 6
            : dados?.taxas[0].prazo
          : dados?.renegociacao_prazo
      ),
      'prazo'
    );
    formOfer.current.setItem(
      !dados?.renegociacao_taxa
        ? dados?.taxa.replace('%', '')
        : dados?.renegociacao_taxa,
      'taxa'
    );
    formOfer.current.setItem(
      !dados?.renegociacao_periodicidade
        ? !dados?.periodicidade
          ? 30
          : dados?.periodicidade
        : dados?.renegociacao_periodicidade,
      'periodicidade'
    );
    formOfer.current.setItem(
      !dados.renegociacao_seguro
        ? !dados.seguro
          ? 'Não'
          : 'Sim'
        : dados.renegociacao_seguro === true
          ? 'Sim'
          : 'Não',
      'seguroSN'
    );
    formOfer.current.setItem(
      dados.renegociacao_seguro_assistencia === true ? 'Sim' : 'Não',
      'seguroAssisSN'
    );
    formOfer.current.setItem(
      dados.renegociacao_iof === true ? 'Sim' : 'Não',
      'iofSN'
    );

    const handler = async () => {
      setTimeout(async () => {
        await calc();
        await calcCet();
      }, 100);
    };
    handler();
  }, [registers, calc, allSelect, recalcula]);

  //funcao do botao para enviar ao cliente.
  const saveCliente = async () => {
    const form = await formOfer.current.getForm?.();

    const id = dados?.id;
    try {
      const titulos_renego = rows
        .filter((item) => item.parcela !== 'Saldo a Vencer')
        .map((item) => {
          if (item.renegociar === true) {
            return {
              ...item,
              id: item?.id,
              parcela: item?.parcela,
              renegociar: item?.renegociar,
            };
          }
          return item;
        });
      //pega o titulo criado para pegar a informacao de selecionado
      const saldoaVencer = rows.filter(
        (item) => item.parcela === 'Saldo a Vencer'
      );
      //pega os titulos em aberto ainda.
      const titulos_aber = dados?.titulos.filter((a) => a.status !== 'vencido');
      //junta todos os titulos em um lugar para atualizar, sem apagar os que ja tem.
      const payloadParcelas = [...titulos_renego, ...titulos_aber];

      //payload para salvar na renegociacao e mandar para o cliente.
      const payloadCobranca = {
        id: dados?.id,
        renegociacao_taxa: Number(form?.taxa),
        renegociacao_periodicidade: Number(form?.periodicidade),
        renegociacao_prazo: Number(form?.prazo),
        renegociacao_seguro: form?.seguroSN === 'Sim' ? true : false,
        renegociacao_seguro_assistencia:
          form?.seguroAssisSN === 'Sim' ? true : false,
        renegociacao_iof: form?.iofSN === 'Sim' ? true : false,
        renegociacao_saldodevedor: saldoaVencer[0].renegociar,
        renegociacao_entrada: form?.valor_entrada,
        carteira: form?.carteira,
        renegociacao_data_1parcela: form?.dias_carencia,
        titulos: payloadParcelas,
        contato_cliente: new Date(),
        renegociacao_tab: form?.tabSN,
        renegociacao_tac: form?.tacSN,
      };

      // payload para envio de mensagem para cliente
      const payload = {
        valor_renegociar: parseCurrency(form.valor_total),
        valor_entrada: parseCurrency(form.valor_entrada),
        periodicidade: form.periodicidade,
        prazo: form.prazo,
        pmt: form.pmt,
        data_1parcela: moment(form.dias_carencia).format('L'),
        telefone: dados.telefone,
        nome: dados.customer,
        banco: dados.banco,
        distribuidor: dados.distributor,
        idcobranca: dados.id,
      };
      //verifica se o cliente ja confirmou a renegociação, se confirmou nao pode enviar nova mensagem
      if (confirma === true) {
        toast.error(
          'Cliente já confirmou a renegociação, não é possível enviar nova mensagem!'
        );
        return;
      }
      //verificar o valor total for negativo nao vai enviar
      if (form.valor_total <= 0) {
        toast.error('Valor renegociado invalido, verifique as informações!');
        setLoading(false);
        return;
      }

      if (!dados.telefone) {
        toast.error('Telefone do cliente não está cadastrado');
        setLoading(false);
        return;
      }

      if (form?.dias_carencia) {
        const formattedDate = moment(
          form?.dias_carencia,
          ['DD/MM/YYYY', 'YYYY-MM-DD'],
          true
        );
        // Verifica se a data é menor que hoje
        if (formattedDate.isBefore(moment().startOf('day'))) {
          toast.error(
            'A data da primeira parcela não pode ser menor que a data atual'
          );
          setLoading(false);
          return;
        }
      }

      const result = await msgRenegociacaoCliente({ data: payload }, id);
      const resultUpdate = await Update({ data: payloadCobranca }, id);

      toast.success('Enviado mensagem ao Cliente com sucesso!');
      close();
      reload?.();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || 'Erro ao salvar');
    } finally {
      setLoading(false);
    }
  };

  //funcao do botao para salvar e fechar.
  const saveFecha = async () => {
    const form = await formOfer.current.getForm?.();
    try {
      setLoading(true);
      //traz as alterações para os titulos removendo o titulo criado Saldo a Vencer
      const titulos_renego = rows
        .filter((item) => item.parcela !== 'Saldo a Vencer')
        .map((item) => {
          if (item.renegociar === true) {
            return {
              ...item,
              id: item?.id,
              parcela: item?.parcela,
              renegociar: item?.renegociar,
            };
          }
          return item;
        });
      //pega o titulo criado para pegar a informacao de selecionado
      const saldoaVencer = rows.filter(
        (item) => item.parcela === 'Saldo a Vencer'
      );
      //pega os titulos em aberto ainda.
      const titulos_aber = dados?.titulos.filter((a) => a.status !== 'vencido');
      //junta todos os titulos em um lugar para atualizar, sem apagar os que ja tem.
      const payloadParcelas = [...titulos_renego, ...titulos_aber];

      const id = dados?.id;
      const payloadCobranca = {
        id: dados?.id,
        renegociacao_taxa: Number(form?.taxa),
        renegociacao_periodicidade: Number(form?.periodicidade),
        renegociacao_prazo: Number(form?.prazo),
        renegociacao_seguro: form?.seguroSN === 'Sim' ? true : false,
        renegociacao_seguro_assistencia:
          form?.seguroAssisSN === 'Sim' ? true : false,
        renegociacao_iof: form?.iofSN === 'Sim' ? true : false,
        renegociacao_saldodevedor: saldoaVencer[0].renegociar,
        renegociacao_entrada: form?.valor_entrada,
        carteira: form?.carteira,
        renegociacao_data_1parcela: form?.dias_carencia,
        titulos: payloadParcelas,
        renegociacao_tab: form?.tabSN,
        renegociacao_tac: form?.tacSN,
      };

      //verificar o valor total for negativo nao vai enviar
      if (form.valor_total < 0) {
        toast.error(
          'Valor renegociado esta negativo, verifique as informações!'
        );
        setLoading(false);
        return;
      }

      if (form?.dias_carencia) {
        const formattedDate = moment(
          form?.dias_carencia,
          ['DD/MM/YYYY', 'YYYY-MM-DD'],
          true
        );
        // Verifica se a data é menor que hoje
        if (formattedDate.isBefore(moment().startOf('day'))) {
          toast.error(
            'A data da primeira parcela não pode ser menor que a data atual'
          );
          setLoading(false);
          return;
        }
      }
      const result = await Update({ data: payloadCobranca }, id);

      toast.success('Registro salvo com sucesso!');
      close();
      reload?.();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || 'Erro ao salvar');
    } finally {
      setLoading(false);
    }
  };

  //conclui a renegociacao.
  const saveConclui = async () => {
    const form = await formOfer.current.getForm?.();
    try {
      const datanew = new Date();
      setLoading(true);
      //traz as alterações para os titulos removendo o titulo criado Saldo a Vencer
      const titulos = rows
        .filter((item) => item.parcela !== 'Saldo a Vencer')
        .map((item) => {
          if (item.renegociar === true) {
            return {
              ...item,
              id: item?.id,
              parcela: item?.parcela,
              renegociar: item?.renegociar,
              data_baixa: datanew.toISOString().split('T')[0],
              valor_recebido: 0,
              status: 'baixado',
              tipo_baixa: 'renegociado',
            };
          }
          return item;
        });

      const textoParcela = rows
        .filter((item) => item.renegociar === true)
        .map((item) => item.parcela)
        .join(';');

      //pega o titulo criado para pegar a informacao de selecionado
      const saldoaVencer = rows.filter(
        (item) => item.parcela === 'Saldo a Vencer'
      );

      const titulos_renego = { titulos: titulos };

      //pega os titulos em aberto ainda.
      const titulos_aber = dados?.titulos
        .filter((a) => a.status !== 'vencido')
        .map((item) => {
          if (saldoaVencer[0].renegociar === true) {
            return {
              ...item,
              id: item?.id,
              parcela: item?.parcela,
              renegociar: item?.renegociar,
              data_baixa: datanew.toISOString().split('T')[0],
              valor_recebido: 0,
              status: 'baixado',
              tipo_baixa: 'renegociado',
            };
          } else {
            return item;
          }
        });

      //junta todos os titulos em um lugar para atualizar, sem apagar os que ja tem.
      const payloadParcelas = [...titulos_renego.titulos, ...titulos_aber];

      const id = dados?.id;
      const payloadCobranca = {
        id: dados?.id,
        renegociacao_taxa: Number(form?.taxa),
        renegociacao_periodicidade: Number(form?.periodicidade),
        renegociacao_prazo: Number(form?.prazo),
        renegociacao_seguro: form?.seguroSN === 'Sim' ? true : false,
        renegociacao_seguro_assistencia:
          form?.seguroAssisSN === 'Sim' ? true : false,
        renegociacao_iof: form?.iofSN === 'Sim' ? true : false,
        renegociacao_saldodevedor: saldoaVencer[0].renegociar,
        renegociacao_entrada: form?.valor_entrada,
        carteira: form?.carteira,
        renegociacao_data_1parcela: form?.dias_carencia,
        confirma_cliente: false,
        contato_cliente: null,
        renegociacao_tab: form?.tabSN,
        renegociacao_tac: form?.tacSN,
      };

      // payload para criacao da nova cobrança
      datanew.setDate(datanew.getDate() + Number(form?.periodicidade));
      const datapmt = datanew.toISOString().split('T')[0];

      const payloadNovaCobranca = {
        cliente: !dados?.clienteid ? null : dados?.clienteid,
        distribuidor: dados?.distribuidorid,
        banco: dados?.bancoid,
        data_pmt: datapmt,
        valor_financiado: form?.valor_total,
        parcelas: form?.prazo,
        valor_pmt: unparseCurrency(form?.pmt),
        origem: 'renegociado',
        banking: !dados?.bankingid ? null : dados?.bankingid,
        proposta_financeira: !dados.propostaid ? null : dados.propostaid,
        cobranca: dados?.id,
        parcelas_origem: textoParcela,
        periodicidade: Number(form?.periodicidade),
        status: 'formalizar',
      };

      //verifica se o cliente ja confirmou a renegociação.
      if (confirma === false) {
        toast.error(
          'Cliente não confirmou a renegociação, não é possível concluir a renegociação!'
        );
        return;
      }

      //verificar o valor total for negativo nao vai enviar
      if (form.valor_total <= 0) {
        toast.error('Valor renegociado invalido, verifique as informações!');
        setLoading(false);
        return;
      }

      if (form?.dias_carencia) {
        const formattedDate = moment(
          form?.dias_carencia,
          ['DD/MM/YYYY', 'YYYY-MM-DD'],
          true
        );
        // Verifica se a data é menor que hoje
        if (formattedDate.isBefore(moment().startOf('day'))) {
          toast.error(
            'A data da primeira parcela não pode ser menor que a data atual'
          );
          setLoading(false);
          return;
        }
      }

      if (form.valor_renegociar === 0) {
        toast.error('Valor renegociado esta zerado!');
        setLoading(false);
        return;
      }

      const resultcreay = await Create({ data: payloadNovaCobranca });
      const resultupdateTitulos = await baixaParcela(
        { data: payloadParcelas },
        id
      );
      const resultupdate = await Update({ data: payloadCobranca }, id);

      toast.success('Registro salvo com sucesso!');
      close();
      reload?.();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || 'Erro ao salvar');
    } finally {
      setLoading(false);
    }
  };

  if (!registers) {
    return null;
  }

  return (
    <WrapperTwoCol
      title={'Renegociação'}
      titleColumn1="Extrato"
      width="80vw"
      children={
        <AddProposalContainer>
          <BasicTable columns={columns} rows={rows} loading={loading} />
        </AddProposalContainer>
      }
      titleColumn2="Oferta"
      children2={
        <AddProposalContainer>
          <FormCore ref={formOfer} formItems={formOferta} />
        </AddProposalContainer>
      }
      footer={
        <div style={{ display: 'grid', width: '100%', gap: '20px' }}>
          <ButtonsAndInputWrapper>
            <CompanyButtonContainer>
              <Button color="secondary" onClick={saveCliente} nospace>
                {loading ? 'Processando' : 'Enviar para o Cliente'}
              </Button>
              <Button onClick={saveFecha} color="blue" nospace>
                {loading ? 'Processando' : 'Fechar e Salvar'}
              </Button>
              <Button onClick={saveConclui} color="green" nospace>
                {loading ? 'Processando' : 'Fechar e Concluir'}
              </Button>
            </CompanyButtonContainer>
          </ButtonsAndInputWrapper>
        </div>
      }
    ></WrapperTwoCol>
  );
}
